import {
  GameDto,
  PlayerDto,
  MoveResultDto,
  CreateGameDto,
  MakeMoveDto,
  LoginDto,
  RegisterDto,
  AuthResponseDto,
  BoardStateDto
} from './types.js';
import { authService } from './auth.js';

// Use relative URL in production, localhost in development
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE_URL = isLocalhost
  ? 'http://localhost:5036/api'
  : '/api';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiClient {
  private accessToken: string | null = null;

  constructor() {
    // Load token from localStorage on initialization
    this.accessToken = localStorage.getItem('accessToken');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...options.headers,
    });

    // Get token from Keycloak if available
    const keycloakToken = authService.getToken();
    if (keycloakToken) {
      headers.set('Authorization', `Bearer ${keycloakToken}`);
    } else if (this.accessToken) {
      // Fall back to stored token if any
      headers.set('Authorization', `Bearer ${this.accessToken}`);
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        throw new ApiError(response.status, errorMessage);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      return {} as T;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, error instanceof Error ? error.message : 'Network error');
    }
  }

  setToken(token: string) {
    this.accessToken = token;
    localStorage.setItem('accessToken', token);
  }

  clearToken() {
    this.accessToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  // Authentication
  async login(credentials: LoginDto): Promise<AuthResponseDto> {
    const response = await this.request<AuthResponseDto>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    this.setToken(response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('user', JSON.stringify(response.player));
    
    return response;
  }

  async guestLogin(): Promise<AuthResponseDto> {
    const guestNumber = Math.floor(Math.random() * 10000) + 1;
    const guestName = `Guest#${guestNumber}`;
    const guestEmail = `guest${guestNumber}@migoyugo.temp`;
    const guestPassword = 'guest123';
    
    try {
      // First try to register the guest user
      const registerData: RegisterDto = {
        username: guestName,
        email: guestEmail,
        password: guestPassword
      };
      
      const response = await this.request<AuthResponseDto>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(registerData),
      });
      
      this.setToken(response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.player));
      
      return response;
    } catch (error) {
      // If registration fails (likely because user already exists), try login
      const loginCredentials: LoginDto = {
        username: guestName,
        password: guestPassword
      };
      
      const response = await this.request<AuthResponseDto>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(loginCredentials),
      });
      
      this.setToken(response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.player));
      
      return response;
    }
  }

  async register(userData: RegisterDto): Promise<AuthResponseDto> {
    const response = await this.request<AuthResponseDto>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    this.setToken(response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('user', JSON.stringify(response.player));
    
    return response;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } finally {
      this.clearToken();
    }
  }

  // Game Management
  async createGame(gameData: CreateGameDto): Promise<GameDto> {
    return this.request<GameDto>('/game', {
      method: 'POST',
      body: JSON.stringify(gameData),
    });
  }

  async getGame(gameId: string): Promise<GameDto> {
    return this.request<GameDto>(`/game/${gameId}`);
  }

  async joinGame(gameId: string): Promise<GameDto> {
    return this.request<GameDto>(`/game/${gameId}/join`, {
      method: 'POST',
    });
  }

  async makeMove(gameId: string, move: MakeMoveDto): Promise<MoveResultDto> {
    console.log('API makeMove called with:', { gameId, move });
    try {
      const result = await this.request<MoveResultDto>(`/game/${gameId}/moves`, {
        method: 'POST',
        body: JSON.stringify(move),
      });
      console.log('API makeMove result:', result);
      return result;
    } catch (error) {
      console.error('API makeMove error:', error);
      throw error;
    }
  }

  async getBoardState(gameId: string): Promise<BoardStateDto> {
    return this.request<BoardStateDto>(`/game/${gameId}/board`);
  }

  async resignGame(gameId: string): Promise<GameDto> {
    return this.request<GameDto>(`/game/${gameId}/resign`, {
      method: 'POST',
    });
  }

  // Utility methods
  getCurrentUser(): PlayerDto | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated(): boolean {
    return !!this.accessToken && !!this.getCurrentUser();
  }

  async refreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return false;

    try {
      const response = await this.request<AuthResponseDto>('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
      
      this.setToken(response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.player));
      
      return true;
    } catch {
      this.clearToken();
      return false;
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export { ApiError };